import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { API_BASE_URL } from '../../config/api';
import './VendorDashboardPage.css';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const VendorDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { planDisplayName } = useSubscription();

  const [stats, setStats] = useState({
    totalProducts: 0,
    totalMessages: 0,
    totalRFQ: 0,
    totalCatalogViews: 0
  });
  const [monthlyMessages, setMonthlyMessages] = useState(new Array(12).fill(0));
  const [monthlyCatalogViews, setMonthlyCatalogViews] = useState(new Array(12).fill(0));
  const [feedTab, setFeedTab] = useState('rfq');
  const [rfqList, setRfqList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [activeSidebar, setActiveSidebar] = useState('dashboard');
  const [optimizationScore] = useState(80);
  const [loading, setLoading] = useState(true);

  const companyName = user?.companyName || 'My Company';
  const userName = user?.name || user?.companyName || 'User';

  // Date range for "This Year"
  const now = new Date();
  const yearAgo = new Date(now);
  yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const dateRangeLabel = `${formatDate(yearAgo)} To ${formatDate(now)}`;

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('token');
      const userId = user?.id || storedUser?.id;
      const sellerId = user?.sellerId || user?.sellerProfileId || storedUser?.sellerId || storedUser?.sellerProfileId;

      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const queryParams = new URLSearchParams();
      if (userId) queryParams.append('userId', userId);
      if (sellerId) queryParams.append('sellerId', sellerId);
      const qs = queryParams.toString();

      const [statsRes, analyticsRes, productsRes, rfqRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/stats${qs ? '?' + qs : ''}`, { headers }),
        fetch(`${API_BASE_URL}/dashboard/analytics${qs ? '?' + qs : ''}`, { headers }),
        fetch(`${API_BASE_URL}/products/user/${userId}?limit=5`, { headers }).catch(() => null),
        sellerId
          ? fetch(`${API_BASE_URL}/rfq/seller/${sellerId}?limit=10`, { headers }).catch(() => null)
          : null
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats({
          totalProducts: data.products?.total || data.summary?.totalProductListing || 0,
          totalMessages: data.messages?.total || data.conversations?.total || data.summary?.totalInquireMessage || 0,
          totalRFQ: data.rfq?.total || data.summary?.totalRFQReceived || 0,
          totalCatalogViews: data.totalCatalogViews || 0
        });
      }

      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        if (data.monthlyMessages) setMonthlyMessages(data.monthlyMessages);
        if (data.monthlyCatalogViews) setMonthlyCatalogViews(data.monthlyCatalogViews);
        if (data.monthly) {
          const msgs = new Array(12).fill(0);
          const views = new Array(12).fill(0);
          data.monthly.forEach(m => {
            const idx = (m.month || 1) - 1;
            msgs[idx] = m.messages || m.inquiries || 0;
            views[idx] = m.catalogViews || m.views || 0;
          });
          setMonthlyMessages(msgs);
          setMonthlyCatalogViews(views);
        }
      }

      if (productsRes && productsRes.ok) {
        const data = await productsRes.json();
        setProductList(Array.isArray(data) ? data.slice(0, 5) : (data.products || []).slice(0, 5));
      }

      if (rfqRes && rfqRes.ok) {
        const data = await rfqRes.json();
        const rfqs = Array.isArray(data) ? data : (data.rfqs || data.data || []);
        setRfqList(rfqs.slice(0, 10));
      }

    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Sidebar items
  const sidebarItems = [
    { id: 'dashboard', icon: 'dashboard', label: 'Dashboard', path: '/vendor-dashboard' },
    { id: 'products', icon: 'products', label: 'Company Profile', path: '/supplier-profile-info' },
    { id: 'rfq', icon: 'rfq', label: 'Product', path: '/product-manage' },
    { id: 'inquiries', icon: 'inquiries', label: 'Quots', path: '/supplier-inquiries' },
    { id: 'messages', icon: 'messages', label: 'Social Setting', path: '/messages' },
    { id: 'ads', icon: 'ads', label: 'RFQs', path: '/rfq-manage' },
    { id: 'eye', icon: 'eye', label: 'Inquiries', path: '/supplier-inquiries' },
    { id: 'chat', icon: 'chat', label: 'Guest Inquiries', path: '/messages' },
    { id: 'membership', icon: 'membership', label: 'Membership Plan', path: '/membership-packages' },
    { id: 'support', icon: 'support', label: 'Support', path: '/my-support' },
    { id: 'settings', icon: 'settings', label: 'Setting', path: '/profile' },
    { id: 'logout', icon: 'logout', label: 'Logout', path: '/' },
  ];

  // Simple SVG Line Chart
  const renderLineChart = (data, color = '#F6A83B') => {
    const max = Math.max(...data, 1);
    const width = 100;
    const height = 60;
    const padding = 5;
    const chartW = width - padding * 2;
    const chartH = height - padding * 2;

    const points = data.map((val, i) => {
      const x = padding + (i / 11) * chartW;
      const y = height - padding - (val / max) * chartH;
      return `${x},${y}`;
    }).join(' ');

    const areaPoints = `${padding},${height - padding} ${points} ${padding + chartW},${height - padding}`;

    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="vd-chart-svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.15" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0,1,2,3,4].map(i => (
          <line key={i} x1={padding} y1={padding + (i/4)*chartH} x2={padding+chartW} y2={padding + (i/4)*chartH} stroke="#eee" strokeWidth="0.3" />
        ))}
        {/* Area fill */}
        <polygon points={areaPoints} fill={`url(#grad-${color.replace('#','')})`} />
        {/* Line */}
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots */}
        {data.map((val, i) => {
          const x = padding + (i / 11) * chartW;
          const y = height - padding - (val / max) * chartH;
          return <circle key={i} cx={x} cy={y} r="1.5" fill={color} stroke="#fff" strokeWidth="0.5" />;
        })}
      </svg>
    );
  };

  const renderSidebarIcon = (icon) => {
    switch(icon) {
      case 'dashboard':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 4H10V10H4V4Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 4H20V10H14V4Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 14H10V20H4V14Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 14H20V20H14V14Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'products':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'rfq':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 3.46957 3 4 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'inquiries':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'messages':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'ads':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 9H21" stroke="currentColor" strokeWidth="1.5"/><path d="M9 21V9" stroke="currentColor" strokeWidth="1.5"/></svg>
        );
      case 'eye':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/></svg>
        );
      case 'chat':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'membership':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'support':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.8214 2.48697 15.5291 3.33782 17L2.5 21.5L7 20.6622C8.47087 21.513 10.1786 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 16V15.5C12 14.12 13 13.5 14 12.83C14.62 12.42 15 11.83 15 11C15 9.34 13.66 8 12 8C10.34 8 9 9.34 9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="18" r="0.5" fill="currentColor"/></svg>
        );
      case 'settings':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      case 'logout':
        return (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        );
      default: return null;
    }
  };

  return (
    <div className="vd-page">
      {/* Left Sidebar */}
      <aside className="vd-sidebar">
        <div className="vd-sidebar-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/logo.png?v=2" alt="Conflux" className="vd-sidebar-logo-img" />
          <span className="vd-sidebar-logo-text">CONFLUX</span>
        </div>
        <nav className="vd-sidebar-nav">
          {sidebarItems.map((item) => (
            <div
              key={item.id}
              className={`vd-sidebar-item ${activeSidebar === item.id ? 'active' : ''} ${item.id === 'logout' ? 'logout' : ''}`}
              onClick={() => {
                if (item.id === 'logout') {
                  handleLogout();
                } else {
                  setActiveSidebar(item.id);
                  navigate(item.path);
                }
              }}
            >
              {renderSidebarIcon(item.icon)}
              <span className="vd-sidebar-item-label">{item.label}</span>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="vd-main">
        {/* Top Header Bar */}
        <header className="vd-header">
          <div className="vd-header-left">
            <span className="vd-welcome">Welcome {userName}</span>
            <Link to="/membership-packages" className="vd-upgrade-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#FF6B00" stroke="#FF6B00" strokeWidth="1.5"/></svg>
              UPGRADE MEMBERSHIP
            </Link>
            <div className="vd-wallet">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="14" rx="2" stroke="#FF6B00" strokeWidth="1.5"/><path d="M2 10H22" stroke="#FF6B00" strokeWidth="1.5"/><circle cx="17" cy="14" r="1" fill="#FF6B00"/></svg>
              <span>$0</span>
            </div>
          </div>
          <div className="vd-header-right">
            <Link to="/" className="vd-header-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Home
            </Link>
            <Link to="/messages" className="vd-header-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 6L12 13L2 6" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Inbox
              {stats.totalMessages > 0 && <span className="vd-badge">{stats.totalMessages}</span>}
            </Link>
            <div className="vd-header-link vd-myaccount">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="#333" strokeWidth="1.5"/></svg>
              My Account
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke="#333" strokeWidth="2"/></svg>
            </div>
          </div>
          <h2 className="vd-company-name">{companyName}</h2>
        </header>

        {/* Dashboard Content */}
        <div className="vd-content">
          <div className="vd-content-left">
            {/* Chart Cards */}
            <div className="vd-charts-row">
              <div className="vd-chart-card">
                <div className="vd-chart-header">
                  <div>
                    <div className="vd-chart-period">This Year</div>
                    <div className="vd-chart-dates">{dateRangeLabel}</div>
                  </div>
                </div>
                <div className="vd-chart-stat">
                  <div className="vd-chart-icon orange">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div>
                    <div className="vd-chart-label">Total Messages</div>
                    <div className="vd-chart-value">{stats.totalMessages}</div>
                  </div>
                </div>
                <div className="vd-chart-area">
                  {renderLineChart(monthlyMessages, '#F6A83B')}
                  <div className="vd-chart-xaxis">
                    {MONTHS.map((m, i) => <span key={i}>{m.slice(0,3)}</span>)}
                  </div>
                </div>
              </div>

              <div className="vd-chart-card">
                <div className="vd-chart-header">
                  <div>
                    <div className="vd-chart-period">This Year</div>
                    <div className="vd-chart-dates">{dateRangeLabel}</div>
                  </div>
                </div>
                <div className="vd-chart-stat">
                  <div className="vd-chart-icon orange">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="18" rx="2" stroke="#fff" strokeWidth="1.5"/><path d="M8 7H16" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 11H16" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 15H12" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </div>
                  <div>
                    <div className="vd-chart-label">Total Catalog Views</div>
                    <div className="vd-chart-value">{stats.totalCatalogViews}</div>
                  </div>
                </div>
                <div className="vd-chart-area">
                  {renderLineChart(monthlyCatalogViews, '#F6A83B')}
                  <div className="vd-chart-xaxis">
                    {MONTHS.map((m, i) => <span key={i}>{m.slice(0,3)}</span>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Feeds Section */}
            <div className="vd-feeds">
              <h3 className="vd-feeds-title">Feeds</h3>
              <div className="vd-feeds-tabs">
                <button className={`vd-feeds-tab ${feedTab === 'rfq' ? 'active' : ''}`} onClick={() => setFeedTab('rfq')}>Buy Leads (RFQ)</button>
                <button className={`vd-feeds-tab ${feedTab === 'products' ? 'active' : ''}`} onClick={() => setFeedTab('products')}>Products</button>
              </div>
              <div className="vd-feeds-content">
                {feedTab === 'rfq' ? (
                  rfqList.length > 0 ? (
                    rfqList.map((rfq, i) => (
                      <div key={i} className="vd-feed-item">{rfq.title || rfq.productName || 'RFQ Item'}</div>
                    ))
                  ) : (
                    <div className="vd-feed-empty">RFQ Not Available</div>
                  )
                ) : (
                  productList.length > 0 ? (
                    productList.map((p, i) => (
                      <div key={i} className="vd-feed-item">{p.name || 'Product'}</div>
                    ))
                  ) : (
                    <div className="vd-feed-empty">No Products Found</div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Right Side Widgets */}
          <div className="vd-content-right">
            {/* Stats Cards Grid */}
            <div className="vd-stats-grid">
              <div className="vd-stat-card" onClick={() => navigate('/product-manage')}>
                <div className="vd-stat-icon orange">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 16V8C20.9996 7.6493 20.9071 7.30483 20.7315 7.00117C20.556 6.6975 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.6975 3.26846 7.00117C3.09294 7.30483 3.00036 7.6493 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.998 12 21.998C12.3511 21.998 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div className="vd-stat-number">{stats.totalProducts}</div>
                <div className="vd-stat-label">Products</div>
              </div>
              <div className="vd-stat-card" onClick={() => navigate('/messages')}>
                <div className="vd-stat-icon orange">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="2" stroke="#FF6B00" strokeWidth="1.5"/><path d="M8 10H16" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 14H12" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </div>
                <div className="vd-stat-number">{stats.totalMessages}</div>
                <div className="vd-stat-label">Messages</div>
              </div>
              <div className="vd-stat-card" onClick={() => navigate('/rfq-manage')}>
                <div className="vd-stat-icon orange">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 16V8C20.9996 7.6493 20.9071 7.30483 20.7315 7.00117C20.556 6.6975 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.6975 3.26846 7.00117C3.09294 7.30483 3.00036 7.6493 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.998 12 21.998C12.3511 21.998 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 12L12 16" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="9" r="1" fill="#FF6B00"/></svg>
                </div>
                <div className="vd-stat-number">{stats.totalRFQ}</div>
                <div className="vd-stat-label">Relevant RFQs</div>
              </div>
              <div className="vd-stat-card">
                <div className="vd-stat-icon orange">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="18" rx="2" stroke="#FF6B00" strokeWidth="1.5"/><path d="M8 7H16" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 11H16" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 15H12" stroke="#FF6B00" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </div>
                <div className="vd-stat-number">{stats.totalCatalogViews}</div>
                <div className="vd-stat-label">Catalog Views</div>
              </div>
            </div>

            {/* Add Product Card */}
            <div className="vd-action-card" onClick={() => navigate('/product-input')}>
              <div className="vd-action-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 5V19" stroke="#FF6B00" strokeWidth="2.5" strokeLinecap="round"/><path d="M5 12H19" stroke="#FF6B00" strokeWidth="2.5" strokeLinecap="round"/></svg>
              </div>
              <div className="vd-action-text">
                <h4>Add Product</h4>
                <p>Promote your products to thousands of buyer worldwide</p>
              </div>
            </div>

            {/* Post Buy Leads Card */}
            <div className="vd-action-card" onClick={() => navigate('/send-inquire')}>
              <div className="vd-action-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#FF6B00" strokeWidth="1.5"/><path d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="#FF6B00" strokeWidth="1.5"/></svg>
              </div>
              <div className="vd-action-text">
                <h4>Post Buy Leads (RFQ)</h4>
                <p>Get quotation from thousands of suppliers. Fast and Free.</p>
              </div>
            </div>

            {/* Recommendations Card */}
            <div className="vd-recommendations-card">
              <h4>Recommendations</h4>
              <div className="vd-rec-score">
                <span className="vd-rec-percent">{optimizationScore}%</span>
                <span className="vd-rec-label">Your optimization score</span>
              </div>
              <div className="vd-rec-bar">
                <div className="vd-rec-bar-fill" style={{ width: `${optimizationScore}%` }}></div>
                <div className="vd-rec-bar-thumb" style={{ left: `${optimizationScore}%` }}></div>
              </div>
              <p className="vd-rec-hint">Please fill your profile details to upgrade your score %</p>
              <div className="vd-rec-url">
                <span>Your Current catalog URL is :</span>
                <a href={`/company-profile/${user?.sellerId || user?.sellerProfileId || ''}`}>
                  {window.location.origin}/company/{companyName.toLowerCase().replace(/\s+/g, '-')}
                </a>
              </div>
            </div>

            {/* Trending Card */}
            <div className="vd-trending-card">
              <h4>Trending</h4>
              <p>Top trends in your categories for the past month.</p>
              <div className="vd-trending-empty">Not Found</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboardPage;
